import { useCallback, useEffect, useMemo, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { getSupabase, isSupabaseReady } from '@/lib/supabase';
import type { AuthResult, HottrUser } from './types';

/**
 * Auth abstraction. Single component contract (`useAuth()`), two
 * implementations under the hood:
 *
 *   - Supabase email/password + OAuth session, when `VITE_SUPABASE_URL`
 *     and `VITE_SUPABASE_ANON_KEY` are set
 *   - localStorage-backed mock, when those vars are missing — so the
 *     prototype boots without a backend
 *
 * The decision is made per render via `isSupabaseReady()`; in practice
 * env vars don't change at runtime so the branch is stable. Both paths
 * return the same `AuthState`, so no component cares which is active.
 */

/* ------------------------------------------------------------------ */
/* Mock backend                                                       */
/* ------------------------------------------------------------------ */

const STORAGE_USERS = 'hottr.auth.users';
const STORAGE_SESSION = 'hottr.auth.session';
const AUTH_EVENT = 'hottr:auth';

interface StoredMockUser extends HottrUser {
  /** Base64-encoded password — mock only, never use a real secret here. */
  passwordBase64: string;
}

const readMockUsers = (): StoredMockUser[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_USERS);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as StoredMockUser[]) : [];
  } catch {
    return [];
  }
};

const writeMockUsers = (users: StoredMockUser[]) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_USERS, JSON.stringify(users));
};

const readMockSession = (): HottrUser | null => {
  if (typeof window === 'undefined') return null;
  const id = window.localStorage.getItem(STORAGE_SESSION);
  if (!id) return null;
  const user = readMockUsers().find((u) => u.id === id);
  if (!user) return null;
  const { passwordBase64: _, ...publicUser } = user;
  return publicUser;
};

const writeMockSession = (id: string | null) => {
  if (typeof window === 'undefined') return;
  if (id) {
    window.localStorage.setItem(STORAGE_SESSION, id);
  } else {
    window.localStorage.removeItem(STORAGE_SESSION);
  }
  // Notify same-tab subscribers — native `storage` only fires cross-tab.
  window.dispatchEvent(new Event(AUTH_EVENT));
};

const hashPassword = (pw: string) =>
  typeof window === 'undefined' ? pw : window.btoa(pw);

const newMockId = () =>
  `mock_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

/* ------------------------------------------------------------------ */
/* Supabase backend                                                   */
/* ------------------------------------------------------------------ */

const mapSupabaseUser = (u: User): HottrUser => ({
  id: u.id,
  email: u.email ?? '',
  createdAt: u.created_at,
});

/**
 * Friendly mapping of Supabase auth error strings → copy we'd actually
 * show a user. Anything unmatched falls through verbatim.
 */
const friendlyError = (msg: string): string => {
  const m = msg.toLowerCase();
  if (m.includes('invalid login credentials')) return 'Wrong email or password.';
  if (m.includes('user already registered')) {
    return 'An account with that email already exists. Log in instead.';
  }
  if (m.includes('email not confirmed')) {
    return 'Confirm your email first — check your inbox for the link.';
  }
  if (m.includes('password should be at least')) {
    return msg; // already a clean sentence from Supabase
  }
  if (m.includes('rate limit')) {
    return 'Too many attempts. Try again in a minute.';
  }
  if (m.includes('token has expired') || m.includes('invalid token')) {
    return 'That code expired or didn’t match. Request a new one.';
  }
  if (m.includes('signups not allowed')) {
    return 'New sign-ups are paused right now.';
  }
  if (m.includes('user not found')) {
    return 'No account with that email. Sign up instead.';
  }
  return msg;
};

/* ------------------------------------------------------------------ */
/* Shared validation                                                  */
/* ------------------------------------------------------------------ */

const isValidEmail = (email: string) =>
  /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());

/** Mock + Supabase both gate signup on a sane password length. */
const MIN_PASSWORD = 8;

/* ------------------------------------------------------------------ */
/* Public hook                                                        */
/* ------------------------------------------------------------------ */

export interface AuthState {
  user: HottrUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  /** True while running on the localStorage mock backend. */
  isMock: boolean;
  signUp(email: string, password: string): Promise<AuthResult>;
  logIn(email: string, password: string): Promise<AuthResult>;
  logOut(): Promise<void>;

  /**
   * Passwordless email-code flow.
   *
   * `requestEmailCode` triggers Supabase to email a 6-digit token via the
   * Magic Link template (which has been customised to embed `{{ .Token }}`
   * instead of a link — see docs/supabase/email-templates/).
   *
   *   - `mode: 'login'` only sends to existing users; unknown emails error.
   *   - `mode: 'signup'` creates the user on first request, then signs
   *     them in once the code is verified.
   *
   * `verifyEmailCode` swaps a code for a session.
   */
  requestEmailCode(email: string, mode: 'login' | 'signup'): Promise<AuthResult>;
  verifyEmailCode(email: string, code: string): Promise<AuthResult>;

  /**
   * Password recovery. `requestPasswordReset` sends a Supabase recovery
   * email; the link lands on `/auth/reset` where `setPassword` swaps the
   * recovery session for a new password.
   */
  requestPasswordReset(email: string): Promise<AuthResult>;
  setPassword(newPassword: string): Promise<AuthResult>;

  /**
   * MFA — TOTP only for now.
   *
   *   - `enrollTotp` returns the QR (otpauth URI) and a one-shot factorId.
   *   - `verifyTotpEnrolment` confirms the user can read codes from their
   *      authenticator app and elevates the factor to active.
   *   - `listFactors` returns enrolled TOTP factors.
   *   - `unenrollFactor` removes one.
   *   - `challengeAndVerify` is the login-time step-up: takes a factorId
   *     and a 6-digit code from the user's authenticator, returns AAL2.
   *   - `currentAal` exposes the current assurance level so the login
   *     flow can decide whether to short-circuit to the challenge page.
   */
  enrollTotp(): Promise<{ ok: boolean; factorId?: string; qr?: string; secret?: string; error?: string }>;
  verifyTotpEnrolment(factorId: string, code: string): Promise<AuthResult>;
  listFactors(): Promise<Array<{ id: string; status: string; friendly_name?: string | null }>>;
  unenrollFactor(factorId: string): Promise<AuthResult>;
  challengeAndVerify(factorId: string, code: string): Promise<AuthResult>;
  currentAal(): Promise<'aal1' | 'aal2' | null>;
}

export const useAuth = (): AuthState => {
  // `isMock` is stable for the page lifetime (env vars don't change),
  // so we don't need it as a dependency past first render.
  const isMock = useMemo(() => !isSupabaseReady(), []);

  const [user, setUser] = useState<HottrUser | null>(() =>
    isMock ? readMockSession() : null,
  );
  // Mock reads localStorage synchronously; Supabase has to await getSession()
  const [loading, setLoading] = useState(!isMock);

  useEffect(() => {
    if (isMock) {
      const refresh = () => setUser(readMockSession());
      window.addEventListener('storage', refresh);
      window.addEventListener(AUTH_EVENT, refresh);
      refresh();
      return () => {
        window.removeEventListener('storage', refresh);
        window.removeEventListener(AUTH_EVENT, refresh);
      };
    }

    // Supabase path
    const supabase = getSupabase();
    if (!supabase) {
      setLoading(false);
      return;
    }

    let mounted = true;

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!mounted) return;
        setUser(data.session?.user ? mapSupabaseUser(data.session.user) : null);
        setLoading(false);
      })
      .catch(() => {
        if (mounted) setLoading(false);
      });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      setUser(session?.user ? mapSupabaseUser(session.user) : null);
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, [isMock]);

  /* -------------------- mutators -------------------- */

  const signUp = useCallback(
    async (email: string, password: string): Promise<AuthResult> => {
      const trimmedEmail = email.trim().toLowerCase();
      if (!isValidEmail(trimmedEmail)) {
        return { ok: false, error: 'That email doesn’t look right.' };
      }
      if (password.length < MIN_PASSWORD) {
        return {
          ok: false,
          error: `Password needs at least ${MIN_PASSWORD} characters.`,
        };
      }

      if (isMock) {
        const users = readMockUsers();
        if (users.some((u) => u.email === trimmedEmail)) {
          return {
            ok: false,
            error: 'An account with that email already exists. Log in instead.',
          };
        }
        const next: StoredMockUser = {
          id: newMockId(),
          email: trimmedEmail,
          passwordBase64: hashPassword(password),
          createdAt: new Date().toISOString(),
        };
        writeMockUsers([...users, next]);
        writeMockSession(next.id);
        return { ok: true };
      }

      const supabase = getSupabase();
      if (!supabase) {
        return { ok: false, error: 'Auth backend unavailable.' };
      }
      const { data, error } = await supabase.auth.signUp({
        email: trimmedEmail,
        password,
        options: {
          // After clicking the confirmation link, Supabase sends users
          // back here. AuthCallbackPage then forwards them to the builder.
          emailRedirectTo:
            typeof window !== 'undefined'
              ? `${window.location.origin}/auth/callback`
              : undefined,
        },
      });
      if (error) {
        return { ok: false, error: friendlyError(error.message) };
      }
      if (data.session) {
        // Email confirmation is OFF in the project — user is logged in.
        return { ok: true };
      }
      // Email confirmation required — user must click the link before
      // a session is issued. Surface it as a friendly success state.
      return {
        ok: true,
        needsConfirmation: true,
      };
    },
    [isMock],
  );

  const logIn = useCallback(
    async (email: string, password: string): Promise<AuthResult> => {
      const trimmedEmail = email.trim().toLowerCase();
      if (!isValidEmail(trimmedEmail)) {
        return { ok: false, error: 'That email doesn’t look right.' };
      }

      if (isMock) {
        const users = readMockUsers();
        const match = users.find(
          (u) =>
            u.email === trimmedEmail &&
            u.passwordBase64 === hashPassword(password),
        );
        if (!match) {
          return { ok: false, error: 'Wrong email or password.' };
        }
        writeMockSession(match.id);
        return { ok: true };
      }

      const supabase = getSupabase();
      if (!supabase) {
        return { ok: false, error: 'Auth backend unavailable.' };
      }
      const { error } = await supabase.auth.signInWithPassword({
        email: trimmedEmail,
        password,
      });
      if (error) {
        return { ok: false, error: friendlyError(error.message) };
      }
      return { ok: true };
    },
    [isMock],
  );

  const logOut = useCallback(async () => {
    if (isMock) {
      writeMockSession(null);
      return;
    }
    const supabase = getSupabase();
    if (supabase) await supabase.auth.signOut();
  }, [isMock]);

  /* -------------------- passwordless OTP -------------------- */

  const requestEmailCode = useCallback(
    async (email: string, mode: 'login' | 'signup'): Promise<AuthResult> => {
      const trimmedEmail = email.trim().toLowerCase();
      if (!isValidEmail(trimmedEmail)) {
        return { ok: false, error: 'That email doesn’t look right.' };
      }

      if (isMock) {
        // Mock backend: accept the email + persist a pending code in
        // localStorage so the dev flow works offline. The "sent" code is
        // 000000 by default — surfaced in the UI via a small dev hint.
        if (mode === 'login') {
          const users = readMockUsers();
          if (!users.some((u) => u.email === trimmedEmail)) {
            return {
              ok: false,
              error: 'No account with that email. Sign up instead.',
            };
          }
        } else {
          const users = readMockUsers();
          if (!users.some((u) => u.email === trimmedEmail)) {
            // Create a placeholder mock user for signup.
            const placeholder: StoredMockUser = {
              id: newMockId(),
              email: trimmedEmail,
              passwordBase64: hashPassword('mock-otp-placeholder'),
              createdAt: new Date().toISOString(),
            };
            writeMockUsers([...users, placeholder]);
          }
        }
        window.localStorage.setItem('hottr.auth.mockPendingCode', '000000');
        window.localStorage.setItem('hottr.auth.mockPendingEmail', trimmedEmail);
        return { ok: true };
      }

      const supabase = getSupabase();
      if (!supabase) {
        return { ok: false, error: 'Auth backend unavailable.' };
      }
      const { error } = await supabase.auth.signInWithOtp({
        email: trimmedEmail,
        options: {
          shouldCreateUser: mode === 'signup',
          // Intentionally no `emailRedirectTo`. HOTTR is code-only — the
          // user types the 6-digit token back into the app; there is no
          // clickable link rendered in the email template. See
          // docs/supabase/email-templates/magic-link.html.
        },
      });
      if (error) {
        // Supabase returns "Signups not allowed for otp" if `shouldCreateUser`
        // is true but signups are disabled at the project level.
        return { ok: false, error: friendlyError(error.message) };
      }
      return { ok: true };
    },
    [isMock],
  );

  const verifyEmailCode = useCallback(
    async (email: string, code: string): Promise<AuthResult> => {
      const trimmedEmail = email.trim().toLowerCase();
      const trimmedCode = code.trim();
      if (trimmedCode.length !== 6 || !/^\d{6}$/.test(trimmedCode)) {
        return { ok: false, error: 'Code must be 6 digits.' };
      }

      if (isMock) {
        const pendingCode = window.localStorage.getItem('hottr.auth.mockPendingCode');
        const pendingEmail = window.localStorage.getItem('hottr.auth.mockPendingEmail');
        if (
          !pendingCode ||
          !pendingEmail ||
          pendingEmail !== trimmedEmail ||
          pendingCode !== trimmedCode
        ) {
          return { ok: false, error: 'Wrong code. Try again.' };
        }
        const users = readMockUsers();
        const match = users.find((u) => u.email === trimmedEmail);
        if (!match) {
          return { ok: false, error: 'Account not found.' };
        }
        writeMockSession(match.id);
        window.localStorage.removeItem('hottr.auth.mockPendingCode');
        window.localStorage.removeItem('hottr.auth.mockPendingEmail');
        return { ok: true };
      }

      const supabase = getSupabase();
      if (!supabase) {
        return { ok: false, error: 'Auth backend unavailable.' };
      }
      const { error } = await supabase.auth.verifyOtp({
        email: trimmedEmail,
        token: trimmedCode,
        type: 'email',
      });
      if (error) {
        return { ok: false, error: friendlyError(error.message) };
      }
      return { ok: true };
    },
    [isMock],
  );

  const requestPasswordReset = useCallback(
    async (email: string): Promise<AuthResult> => {
      const trimmedEmail = email.trim().toLowerCase();
      if (!isValidEmail(trimmedEmail)) {
        return { ok: false, error: 'That email doesn’t look right.' };
      }
      if (isMock) {
        // Mock backend: pretend the email was sent. The reset page works
        // against the mock by reading the email from a localStorage hint.
        window.localStorage.setItem('hottr.auth.mockResetEmail', trimmedEmail);
        return { ok: true };
      }
      const supabase = getSupabase();
      if (!supabase) return { ok: false, error: 'Auth backend unavailable.' };
      const { error } = await supabase.auth.resetPasswordForEmail(trimmedEmail, {
        redirectTo:
          typeof window !== 'undefined'
            ? `${window.location.origin}/auth/reset`
            : undefined,
      });
      if (error) return { ok: false, error: friendlyError(error.message) };
      return { ok: true };
    },
    [isMock],
  );

  const setPassword = useCallback(
    async (newPassword: string): Promise<AuthResult> => {
      if (newPassword.length < MIN_PASSWORD) {
        return {
          ok: false,
          error: `Password needs at least ${MIN_PASSWORD} characters.`,
        };
      }
      if (isMock) {
        // Mock backend: rewrite the most recently reset-requested user.
        const email = window.localStorage.getItem('hottr.auth.mockResetEmail');
        if (!email) return { ok: false, error: 'No password-reset request found.' };
        const users = readMockUsers();
        const idx = users.findIndex((u) => u.email === email);
        if (idx < 0) return { ok: false, error: 'Account not found.' };
        users[idx] = { ...users[idx], passwordBase64: hashPassword(newPassword) };
        writeMockUsers(users);
        writeMockSession(users[idx].id);
        window.localStorage.removeItem('hottr.auth.mockResetEmail');
        return { ok: true };
      }
      const supabase = getSupabase();
      if (!supabase) return { ok: false, error: 'Auth backend unavailable.' };
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) return { ok: false, error: friendlyError(error.message) };
      return { ok: true };
    },
    [isMock],
  );

  /* -------------------- MFA / TOTP -------------------- */

  const enrollTotp = useCallback(async () => {
    if (isMock) {
      // Mock mode — return a stable QR + secret pair, accept "000000".
      return {
        ok: true,
        factorId: 'mock-factor',
        qr: 'otpauth://totp/HOTTR.:mock@hottr.app?secret=JBSWY3DPEHPK3PXP&issuer=HOTTR.',
        secret: 'JBSWY3DPEHPK3PXP',
      };
    }
    const supabase = getSupabase();
    if (!supabase) return { ok: false, error: 'Auth backend unavailable.' };
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: 'totp',
      friendlyName: 'HOTTR.',
    });
    if (error) return { ok: false, error: friendlyError(error.message) };
    return { ok: true, factorId: data.id, qr: data.totp.uri, secret: data.totp.secret };
  }, [isMock]);

  const verifyTotpEnrolment = useCallback(
    async (factorId: string, code: string): Promise<AuthResult> => {
      if (isMock) {
        return code === '000000'
          ? { ok: true }
          : { ok: false, error: 'Wrong code. Try again.' };
      }
      const supabase = getSupabase();
      if (!supabase) return { ok: false, error: 'Auth backend unavailable.' };
      const { data: challenge, error: chErr } = await supabase.auth.mfa.challenge({ factorId });
      if (chErr) return { ok: false, error: friendlyError(chErr.message) };
      const { error: vErr } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code,
      });
      if (vErr) return { ok: false, error: friendlyError(vErr.message) };
      return { ok: true };
    },
    [isMock],
  );

  const listFactors = useCallback(async () => {
    if (isMock) return [];
    const supabase = getSupabase();
    if (!supabase) return [];
    const { data } = await supabase.auth.mfa.listFactors();
    return (data?.totp ?? []).map((f) => ({
      id: f.id,
      status: f.status,
      friendly_name: f.friendly_name,
    }));
  }, [isMock]);

  const unenrollFactor = useCallback(
    async (factorId: string): Promise<AuthResult> => {
      if (isMock) return { ok: true };
      const supabase = getSupabase();
      if (!supabase) return { ok: false, error: 'Auth backend unavailable.' };
      const { error } = await supabase.auth.mfa.unenroll({ factorId });
      if (error) return { ok: false, error: friendlyError(error.message) };
      return { ok: true };
    },
    [isMock],
  );

  const challengeAndVerify = useCallback(
    async (factorId: string, code: string): Promise<AuthResult> => {
      if (isMock) {
        return code === '000000'
          ? { ok: true }
          : { ok: false, error: 'Wrong code. Try again.' };
      }
      const supabase = getSupabase();
      if (!supabase) return { ok: false, error: 'Auth backend unavailable.' };
      const { data: ch, error: chErr } = await supabase.auth.mfa.challenge({ factorId });
      if (chErr) return { ok: false, error: friendlyError(chErr.message) };
      const { error: vErr } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: ch.id,
        code,
      });
      if (vErr) return { ok: false, error: friendlyError(vErr.message) };
      return { ok: true };
    },
    [isMock],
  );

  const currentAal = useCallback(async (): Promise<'aal1' | 'aal2' | null> => {
    if (isMock) return 'aal1';
    const supabase = getSupabase();
    if (!supabase) return null;
    const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    return (data?.currentLevel as 'aal1' | 'aal2' | null) ?? null;
  }, [isMock]);

  return {
    user,
    loading,
    isAuthenticated: user !== null,
    isMock,
    signUp,
    logIn,
    logOut,
    requestEmailCode,
    verifyEmailCode,
    requestPasswordReset,
    setPassword,
    enrollTotp,
    verifyTotpEnrolment,
    listFactors,
    unenrollFactor,
    challengeAndVerify,
    currentAal,
  };
};
